'use client';

import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DonationFragment } from '../../lib/graphql/types';
import { Page } from '../../lib/pageData';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import { SupporterDetailsProps } from '../SupporterDetails/SupporterDetails';
import { SupportersList } from '../SupportersList/SupportersList';
import { donationFragment } from '../../lib/graphql/gqlPageDataQuery';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import { whenOwnerType } from '../../utils/ownerType';

const fetchNextPageQuery = /* GraphQL */ `
  ${donationFragment}

  query DirectSupportersListNextPage(
    $pageId: ID
    $after: String!
    $pageType: PageType
  ) {
    page(id: $pageId, type: $pageType) {
      supporters: donations(first: 5, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          ...Donation
        }
      }
    }
  }
`;

type DirectSupportersListProps = {
  page: Page;
};

export const DirectSupportersList: FunctionComponent<
  DirectSupportersListProps
> = ({ page }) => {
  const { t } = useTranslation();

  // Group pages don't support direct donations. Display members count instead of direct donation count
  const supporterCount = whenOwnerType(page, {
    isIndividual: () => page.donationSummary?.donationCount,
    isOrganisation: () => page.donationSummary?.donationCount,
    isPeerGroup: () =>
      page.donationSummary?.onlineDonationCount ?? page.donations?.totalCount, // legacy in-mem collection pages read the value from donations.totalCount
  });

  return (
    <SupportersList<DonationFragment[]>
      page={page}
      supporters={page.donations}
      supporterCount={supporterCount ?? 0}
      fetchNextPageQuery={fetchNextPageQuery}
      supporterToSupporterDetailsMapper={mapDonationsToSupporterDetails}
    >
      <SupportersList.Title>
        {t('supporters.direct.title')}
      </SupportersList.Title>
      <SupportersList.Supporters variant="direct" />
      <SupportersList.Placeholder>
        {whenOwnerType(page, {
          isIndividual: () =>
            t('fundraising.noSupportersMessage', {
              name: page.owner?.name ?? '',
            }),
          isOrganisation: () => t('campaign.noSupportersMessage'),
          isPeerGroup: () =>
            t('fundraising.noSupportersMessage', {
              name: page.owner?.name ?? '',
            }),
        })}
      </SupportersList.Placeholder>
      <SupportersList.LoadMore>
        <Trans i18nKey="supporters.direct.showMore">
          Show more
          <span className="cp-sr-only"> supporters</span>
        </Trans>
      </SupportersList.LoadMore>
      {whenOwnerType(page, {
        isIndividual: () => <SupportersList.Donate variant="direct" />,
        isOrganisation: () => <SupportersList.Donate variant="direct" />,
        isPeerGroup: () => <SupportersList.Donate variant="group" />,
      })}
      <SupportersList.Error>
        {t('supporters.showMoreError')}
      </SupportersList.Error>
    </SupportersList>
  );
};

const mapDonationsToSupporterDetails = (
  donations?: DonationFragment[] | null,
): ({ id: string } & SupporterDetailsProps)[] =>
  (donations ?? []).map((donation) => ({
    id: donation.legacyId,
    date: donation.creationDate,
    userName: donation.displayName,
    image: donation.avatar
      ? makePhotoUrl(donation.avatar, SIZE_136_136)
      : undefined,
    message: donation.message,
    currencyCode: donation.amount?.currencyCode ?? 'GBP',
    donationAmount: (donation.amount?.value ?? 0) / 100,
    giftAidAmount: (donation.giftAidAmount?.value ?? 0) / 100,
  }));
